import moment from "moment-timezone";
import FormElement, { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import Button from "../../../../components/Button";
import Field from "../../../../components/Field";
import BaseSettingsFormContent from "../../../../components/Form/BaseSettingsFormContent";
import CustomTimeInput from "../../../../components/Inputs/CustomTimeInput";
import Switch from "../../../../components/Inputs/Switch";
import WeekdaySelect from "../../../../components/Inputs/WeekdaySelect";
import { combineDateAndTime } from "../../../../utils/helpers/date";
import { generateErrorsConfigForForm } from "../../../../utils/helpers/errors";
import { firstToUpper } from "../../../../utils/helpers/string";
import { useModuleAccess } from "../../../../utils/hooks/access";
import { useCompanyTimeZone, useIsMasterCompany } from "../../../../utils/hooks/company";
import { useIsFieldDisabled } from "../../helpers/useIsFieldDisabled";

function Form({ mode, values, error, loading, submit, close }) {
	const { t } = useTranslation();
	const [form] = FormElement.useForm();
	const isMasterCompany = useIsMasterCompany();
	const { access } = useModuleAccess("settings.general.workingShifts");
	const disabled = useIsFieldDisabled({ ...access, mode });

	const timezone = useCompanyTimeZone();
	const onFinish = useCallback((values) => {
		values.startDate = values.allDay
			? null
			: combineDateAndTime(
				moment(),
				moment(values.startDate),
				timezone,
			).toISOString(true);
		values.endDate = values.allDay
			? null
			: combineDateAndTime(
				moment(),
				moment(values.endDate),
				timezone,
			).toISOString(true);
		submit(values);
	}, [timezone, submit]);

	const onClose = useCallback(() => {
		form.resetFields();
		close();
	}, [form, close]);

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue({
			status: "active",
			...values,
			allDay: Boolean(values?.allDay),
			startDate: values?.startDate
				? moment.parseZone(values.startDate).utcOffset(moment().utcOffset(), true).toISOString(true)
				: undefined,
			endDate: values?.endDate
				? moment.parseZone(values.endDate).utcOffset(moment().utcOffset(), true).toISOString(true)
				: undefined,
		});
	}, [form, values, timezone]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			["code", "description", "status", "allDay"],
			error,
		);
		form.setFields(fieldErrors);
	}, [error, form]);

	return (
		<FormElement
			form={form}
			className="d-flex flex-column justify-content-between h-100"
			onFinish={onFinish}
		>
			<div>
				<BaseSettingsFormContent
					mode={mode}
					disabled={disabled || !isMasterCompany}
				/>

				<Field
					name="allDay"
					label={t("all-day")}
					valuePropName="checked"
				>
					<Switch />
				</Field>

				<WeekdaySelect
					mode="multiple"
					name="days"
					label={firstToUpper(t("days"))}
					rules={[
						{
							required: true,
							message: t("required-days"),
						},
					]}
				/>

				<BaseField shouldUpdate>
					{({ }, { }, { getFieldValue }) => {
						const allDay = getFieldValue("allDay");
						if (allDay) {
							return null;
						}
						return (
							<Row>
								<Col md={6}>
									<Field
										name="startDate"
										label={t("shift-start")}
										rules={[
											{
												required: true,
												message: t("shift-start-required"),
											},
										]}
									>
										<CustomTimeInput />
									</Field>
								</Col>

								<Col md={6}>
									<Field
										name="endDate"
										label={t("shift-end")}
										rules={[
											{
												required: true,
												message: t("shift-start-required"),
											},
										]}
										dependencies={["startDate"]}
									>
										<CustomTimeInput />
									</Field>
								</Col>
							</Row>
						);
					}}
				</BaseField>
			</div>

			<div className="d-flex justify-content-end">
				<Button
					onClick={onClose}
					disabled={loading}
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
				>
					{t("cancel")}
				</Button>

				{(!disabled || isMasterCompany) && (
					<Button
						type="submit"
						className="btn-dark btn-sm shadow-none"
						loading={loading}
					>
						{t("save")}
					</Button>
				)}
			</div>
		</FormElement>
	);
}

export default Form;
